import { Col, Row, Space } from 'antd';
import useDeclarations from 'hooks/useDeclarations';
import useProducts from 'hooks/useProducts';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { DeclarationExternalEntity } from 'store/declarations/enums/common/declaration-external-entity';
import { EnsGoodsShipmentItem } from 'store/declarations/ireland/entry-summary-declaration';
import { CdsGoodsItemEntry } from 'store/declarations/uk/cds-declaration';
import IrelandProductsTable from 'views/declarations/ireland/common/IrelandProductsTable';
import EnsProductsTable from 'views/declarations/ireland/ens/cards/products/EnsProductsTable';
import UkProductsTable from 'views/declarations/uk/common/UkProductsTable';
import UkNctsProductsTable from 'views/declarations/uk/common/UkNctsProductsTable';
import { ListPayload } from 'core/http/response';
import { ALL_ITEMS_PAGE_SIZE, PaginatedParams } from 'core/http/pagination';
import { debounce } from 'lodash';
import { getErrorMessage } from 'utils/getErrorMessage';
import Notification from 'components/ui/base/notification/Notification';
import useSession from 'hooks/useSession';
import { checkIsAccountsAssociate, checkIsFreightAdmin, checkIsFreightForwarder } from 'config/roles/roles';
import useIndividuals from 'hooks/useIndividuals';
import { Individual } from 'store/individuals/individual';
import useProductsTemplates from 'hooks/useProductsTemplates';
import useFormUtils from 'hooks/useFormUtils';
import useItemFieldTemplates from 'hooks/useItemFieldTemplates';
import { useProductContext } from '../../context/ProductContext';
import useDeclarationFormErrors from 'hooks/useDeclarationFormErrors';
import { handleAddDeclarationProductToProductTemplates } from 'views/custom-declaration/products/productTemplateUtils';
import { getTableChangeParams, TableChangeParams } from 'utils/tableHelpers';
import AssignTeamToProductModal from './AssignTeamToProductModal';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import DeleteProductsModal from './DeleteProductsModal';
import ApplyItemTemplateDrawer from './apply-item-template-drawer/ApplyItemTemplateDrawer';
import ProductsTotal from './products-total/ProductsTotal';
import { SBox44Button } from 'views/declarations/common/box44/components/styles';
import { StickyContainer } from 'components/new-ui/base/wrappers/styles';
import useGetDeclarationMapValues from 'hooks/useGetDeclarationMapValues';
import getRejectedItemErrorCounts from './getRejectedItemErrorCounts';
import getErrorCountsByRow from './getErrorCountsByRow';
import useSubmittedDeclaration from './useSubmittedDeclaration';
import getItemIdsWithErrors from './getItemIdsWithErrors';
import filterProductsWithErrors from './filterProductsWithErrors';
import ShowItemsWithErrorsSwitch from './ShowItemsWithErrorsSwitch';
import ErrorsCheckStatus from './ErrorsCheckStatus';

export type ProductsErrors = { [index: number]: boolean };
type TeamModalResult = { action: 'confirm'; teamId: string } | { action: 'skip' } | { action: 'abort' };
type DeleteRequest = { type: 'single'; productId: string } | { type: 'bulk'; productIds: string[] };

const PRODUCTS_PAGE_MAX_HEIGHT = 'calc(100vh - 26.3rem)';

const Products: FC<{}> = () => {
    const { userInfo } = useSession();
    const { getIndividual } = useIndividuals();
    const { onEdit, viewOnly, setActiveProductParams, revalidateItems } = useProductsContext();
    const { declarationId } = useParams();
    const { declaration } = useDeclarations();
    const { products, listDeclarationProducts, deleteDeclarationProduct, deleteIrelantImportProductsBulk, isLoading } =
        useProducts();
    const { saveProductTemplate } = useProductsTemplates({});
    const { setProductId } = useProductContext();
    const { countryLowerCase: country, formType, internalType, isAis, isCdsExportB1 } = useFormUtils();
    const { declarationErrors } = useDeclarationFormErrors();
    const { applyTemplate } = useItemFieldTemplates();
    const { itemPath } = useGetDeclarationMapValues();

    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [showTeamConfirmationModal, setShowTeamConfirmationModal] = useState(false);
    const [deleteRequest, setDeleteRequest] = useState<DeleteRequest | null>(null);
    const [isApplyDrawerOpen, setIsApplyDrawerOpen] = useState(false);
    const [totalRefreshKey, setTotalRefreshKey] = useState(0);
    const [showOnlyItemsWithErrors, setShowOnlyItemsWithErrors] = useState(false);
    const [isRevalidatingItems, setIsRevalidatingItems] = useState(false);

    const teamModalResolveRef = useRef<((res: TeamModalResult) => void) | null>(null);
    const activeParamsRef = useRef<Partial<PaginatedParams> | undefined>(undefined);

    const isFreightAdmin = checkIsFreightAdmin(userInfo);
    const isAccountsAssociate = checkIsAccountsAssociate(userInfo);
    const shouldPromptTeamSelection = isFreightAdmin || isAccountsAssociate;

    const { submittedDeclaration, isHistoryLoaded } = useSubmittedDeclaration();

    // A rejected declaration marks the items customs rejected from the moment it is opened, without
    // waiting for a submission to validate it. Until the history is in, the positions could still be
    // read against the wrong items, so nothing is marked yet.
    const rejectedErrorCountByItemId = useMemo(
        () => (isHistoryLoaded ? getRejectedItemErrorCounts(declaration, itemPath, submittedDeclaration) : {}),
        [isHistoryLoaded, declaration, itemPath, submittedDeclaration]
    );

    const itemIdsWithErrors = useMemo(
        () => getItemIdsWithErrors(declarationErrors.items, rejectedErrorCountByItemId),
        [declarationErrors.items, rejectedErrorCountByItemId]
    );

    // Only the UK and Ireland tables count the errors per item - the others only mark a row.
    const canShowOnlyItemsWithErrors =
        declaration?.declarationExternalEntity === DeclarationExternalEntity.CDS ||
        declaration?.declarationExternalEntity === DeclarationExternalEntity.REVENUE;
    const isShowingOnlyItemsWithErrors = canShowOnlyItemsWithErrors && showOnlyItemsWithErrors;
    const isShowItemsWithErrorsSwitchVisible =
        canShowOnlyItemsWithErrors && (itemIdsWithErrors.size > 0 || showOnlyItemsWithErrors);
    // The customs errors wait for the history, and the validation errors are worked out again after a
    // deletion - until both are in, a row without a count may still have errors.
    const isCheckingErrors = canShowOnlyItemsWithErrors && (!isHistoryLoaded || isRevalidatingItems);

    const visibleProducts = useMemo(
        () =>
            isShowingOnlyItemsWithErrors && products ? filterProductsWithErrors(products, itemIdsWithErrors) : products,
        [isShowingOnlyItemsWithErrors, products, itemIdsWithErrors]
    );

    const errorCounts = useMemo(
        () => getErrorCountsByRow(visibleProducts?.list ?? [], declarationErrors.items, rejectedErrorCountByItemId),
        [declarationErrors.items, visibleProducts?.list, rejectedErrorCountByItemId]
    );

    // The UK NCTS and ENS tables only mark a row, without a count - marked when either source counted an error.
    const errors = useMemo(() => {
        const errorsByRow: ProductsErrors = {};
        Object.keys(errorCounts).forEach((rowIndex) => (errorsByRow[Number(rowIndex)] = true));
        return errorsByRow;
    }, [errorCounts]);

    const promptTeamId = useCallback(() => {
        // Prevent overwriting resolver if modal is already open
        if (teamModalResolveRef.current) {
            return Promise.resolve<TeamModalResult>({ action: 'abort' });
        }

        setShowTeamConfirmationModal(true);

        return new Promise<TeamModalResult>((resolve) => {
            teamModalResolveRef.current = resolve;
        });
    }, []);

    useEffect(() => {
        setProductId?.(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // For now, we only have bulk delete for Ireland Import (AIS)
    const deleteSelectedProducts = useCallback(async () => {
        try {
            if (!isAis) throw new Error('Bulk delete only implemented for AIS');
            if (!declarationId) throw new Error('Declaration Id is missing');
            await deleteIrelantImportProductsBulk(selectedRowKeys.map(String), declarationId);

            setSelectedRowKeys([]);

            Notification({
                type: 'success',
                messageTitle: 'Operation Successful',
                description: 'Successfully removed products from declaration.',
                placement: 'top',
            });
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                type: 'error',
                messageTitle: 'Operation Failed',
                description: errorMessage || 'Bulk delete failed. Please try again.',
                placement: 'top',
            });
        }
    }, [isAis, declarationId, deleteIrelantImportProductsBulk, selectedRowKeys]);

    const deleteProduct = useCallback(
        async (productId: string) => {
            if (!declarationId) return;

            await deleteDeclarationProduct({
                productId,
                declarationId,
                formType,
                country,
                internalType,
            });
        },
        [country, declarationId, deleteDeclarationProduct, formType, internalType]
    );

    // Brings the table and the listed errors up to date with the deletion. It runs after the
    // confirmation is closed rather than before: the table shows its own loading state, and the
    // reloads and the revalidation would otherwise keep the modal open for all of their duration.
    const refreshAfterDelete = useCallback(async () => {
        if (!declarationId) return;

        const response = await listDeclarationProducts({
            declarationId,
            formType,
            internalType,
            country,
            params: activeParamsRef.current,
        });

        // The deletion emptied the last page, so the table moves back one.
        if (response?.list?.length === 0 && activeParamsRef.current?.page) {
            const prevPageParams = { ...activeParamsRef.current, page: activeParamsRef.current.page - 1 };
            activeParamsRef.current = prevPageParams;
            setActiveProductParams?.(prevPageParams);
            await listDeclarationProducts({
                declarationId,
                formType,
                internalType,
                country,
                params: prevPageParams,
            });
        }

        setIsRevalidatingItems(true);
        try {
            await revalidateItems?.();
        } finally {
            setIsRevalidatingItems(false);
        }
    }, [
        country,
        declarationId,
        formType,
        internalType,
        listDeclarationProducts,
        revalidateItems,
        setActiveProductParams,
    ]);

    const requestDelete = useCallback((productId: string) => {
        setDeleteRequest({ type: 'single', productId });
    }, []);

    const requestBulkDelete = () => {
        setDeleteRequest({ type: 'bulk', productIds: selectedRowKeys.map(String) });
    };

    const confirmDelete = async () => {
        if (!deleteRequest) return;

        try {
            if (deleteRequest.type === 'single') {
                await deleteProduct(deleteRequest.productId);
            } else {
                await deleteSelectedProducts();
            }
            setTotalRefreshKey((key) => key + 1);
        } finally {
            setDeleteRequest(null);
        }

        refreshAfterDelete().catch((error: unknown) => console.error('Refreshing after delete failed:', error));
    };

    const handleApplyTemplate = async (templateId: string) => {
        if (!declarationId) return;

        const applied = await applyTemplate(declarationId, templateId, selectedRowKeys.map(String));
        // On failure the drawer stays open, so the user can try again
        if (!applied) return;

        setTotalRefreshKey((key) => key + 1);

        // Reload the current page, so the products show the applied template and its fields
        listDeclarationProducts?.({
            declarationId,
            formType,
            internalType,
            country,
            params: activeParamsRef.current,
        });
        setIsApplyDrawerOpen(false);
        setSelectedRowKeys([]);
    };

    const getAddDeclarationProductToProductTemplate = useCallback(
        async (productId: string) => {
            if (!shouldPromptTeamSelection) {
                // If the user has a teamId, use it
                let currentTeamId = null;
                const isFreightForwarderRole = checkIsFreightForwarder(userInfo);

                if (isFreightForwarderRole) {
                    if (!userInfo?.individualId) return;

                    const individual: Individual = await getIndividual(userInfo?.individualId);
                    currentTeamId = individual?.teamMemberships[0].teamId; // Currently, indivudal can have only one team
                }

                handleAddDeclarationProductToProductTemplates(
                    productId,
                    products,
                    saveProductTemplate,
                    declaration?.templateId,
                    Boolean(declaration?.cdsDeclaration),
                    currentTeamId
                );
                return;
            }

            // Show the modal and await for user action
            const promptResult = await promptTeamId();

            if (promptResult.action === 'abort') return;

            const teamId = promptResult.action === 'confirm' ? promptResult.teamId : null;

            handleAddDeclarationProductToProductTemplates(
                productId,
                products,
                saveProductTemplate,
                declaration?.templateId,
                Boolean(declaration?.cdsDeclaration),
                teamId
            );
        },
        [
            shouldPromptTeamSelection,
            promptTeamId,
            products,
            saveProductTemplate,
            declaration?.templateId,
            declaration?.cdsDeclaration,
            userInfo,
            getIndividual,
        ]
    );

    const onChange = useCallback(
        ({ pagination, sorter }: TableChangeParams) => {
            if (!declarationId) return;
            const tableParams = getTableChangeParams({ sorter, pagination });
            // The filter runs over the listed items, so while it is on every item stays listed.
            const paginationParams = isShowingOnlyItemsWithErrors
                ? { ...tableParams, page: 0, size: ALL_ITEMS_PAGE_SIZE }
                : tableParams;
            const params: Partial<PaginatedParams> = activeParamsRef.current?.query
                ? { ...paginationParams, query: activeParamsRef.current.query }
                : paginationParams;
            activeParamsRef.current = params;
            setActiveProductParams?.(params);
            listDeclarationProducts?.({ declarationId, formType, internalType, country, params });
        },

        [
            declarationId,
            listDeclarationProducts,
            formType,
            internalType,
            country,
            setActiveProductParams,
            isShowingOnlyItemsWithErrors,
        ]
    );

    const toggleShowOnlyItemsWithErrors = (isOn: boolean) => {
        setSelectedRowKeys([]);
        setShowOnlyItemsWithErrors(isOn);

        // Items with errors may be on pages that are not listed, so every item is listed first.
        const isEveryItemListed = !products || products.list.length >= products.total;
        if (!isOn || isEveryItemListed || !declarationId) return;

        const params: Partial<PaginatedParams> = { ...activeParamsRef.current, page: 0, size: ALL_ITEMS_PAGE_SIZE };
        activeParamsRef.current = params;
        setActiveProductParams?.(params);
        listDeclarationProducts({ declarationId, formType, internalType, country, params });
    };

    const debouncedSearch = debounce((query: string) => {
        if (!declarationId) return;
        const params: Partial<PaginatedParams> = { query, size: activeParamsRef.current?.size };
        activeParamsRef.current = params;
        setActiveProductParams?.(params);
        listDeclarationProducts?.({ declarationId, formType, internalType, country, params });
    }, 700);

    const container = useMemo(() => {
        if (declaration) {
            switch (declaration.declarationExternalEntity) {
                case DeclarationExternalEntity.REVENUE: {
                    return (
                        <IrelandProductsTable
                            loading={isLoading}
                            errorCounts={errorCounts}
                            data={visibleProducts}
                            selectedRowKeys={selectedRowKeys}
                            onSelectedRowKeysChange={setSelectedRowKeys}
                            onDelete={requestDelete}
                            onEdit={onEdit}
                            onAddToTemplate={getAddDeclarationProductToProductTemplate}
                            viewOnly={viewOnly}
                            onChange={onChange}
                        />
                    );
                }
                case DeclarationExternalEntity.CDS:
                    return (
                        <UkProductsTable
                            errorCounts={errorCounts}
                            data={visibleProducts as ListPayload<CdsGoodsItemEntry>}
                            onDelete={requestDelete}
                            onEdit={onEdit}
                            onAddToTemplate={getAddDeclarationProductToProductTemplate}
                            viewOnly={viewOnly}
                            onChange={onChange}
                            selectedRowKeys={selectedRowKeys}
                            onSelectedRowKeysChange={setSelectedRowKeys}
                            showAppliedTemplates={isCdsExportB1}
                        />
                    );
                case DeclarationExternalEntity.HMRC:
                    return (
                        <UkNctsProductsTable
                            errors={errors}
                            data={products as ListPayload<any>}
                            onDelete={requestDelete}
                            onEdit={onEdit}
                            onAddToTemplate={getAddDeclarationProductToProductTemplate}
                            viewOnly={viewOnly}
                            onChange={onChange}
                        />
                    );
                case DeclarationExternalEntity.ICS2:
                    return (
                        <EnsProductsTable
                            errors={errors}
                            data={products as ListPayload<EnsGoodsShipmentItem>}
                            onDelete={requestDelete}
                            viewOnly={viewOnly}
                            onEdit={onEdit}
                            onAddToTemplate={getAddDeclarationProductToProductTemplate}
                            onChange={onChange}
                        />
                    );

                default:
                    <></>;
            }
        }
        return <></>;
    }, [
        declaration,
        errors,
        errorCounts,
        products,
        visibleProducts,
        requestDelete,
        onEdit,
        getAddDeclarationProductToProductTemplate,
        viewOnly,
        onChange,
        isCdsExportB1,
        isLoading,
        selectedRowKeys,
    ]);

    return (
        <div style={{ overflowY: 'auto', maxHeight: PRODUCTS_PAGE_MAX_HEIGHT }}>
            <Row wrap={false} style={{ marginBottom: '2.5rem', columnGap: 16 }}>
                <Col flex="auto">
                    <SearchBar onSearch={(value) => debouncedSearch(value)} inputPlaceholder="Search" />
                </Col>
                <ProductsTotal declarationId={declarationId} refreshKey={totalRefreshKey} />
            </Row>

            {(isShowItemsWithErrorsSwitchVisible || isCheckingErrors) && (
                <Space size={16} align="center">
                    {isShowItemsWithErrorsSwitchVisible && (
                        <ShowItemsWithErrorsSwitch
                            checked={showOnlyItemsWithErrors}
                            onChange={toggleShowOnlyItemsWithErrors}
                            loading={isLoading}
                            disabled={isCheckingErrors}
                        />
                    )}
                    {isCheckingErrors && <ErrorsCheckStatus />}
                </Space>
            )}

            {Boolean(selectedRowKeys.length) && (isAis || isCdsExportB1) && (
                <StickyContainer>
                    <Space style={{ marginBottom: 16, width: '100%' }} size={8}>
                        {isAis && Boolean(selectedRowKeys.length) && (
                            <SBox44Button onClick={requestBulkDelete} loading={isLoading} type="default">
                                Delete Products
                            </SBox44Button>
                        )}
                        {isCdsExportB1 && Boolean(selectedRowKeys.length) && (
                            <SBox44Button onClick={() => setIsApplyDrawerOpen(true)} loading={false} type="default">
                                Apply Item Template
                            </SBox44Button>
                        )}
                    </Space>
                </StickyContainer>
            )}

            {container}

            <DeleteProductsModal
                open={deleteRequest !== null}
                productCount={deleteRequest?.type === 'bulk' ? deleteRequest.productIds.length : 1}
                loading={isLoading}
                onCancel={() => setDeleteRequest(null)}
                onConfirm={confirmDelete}
            />

            {shouldPromptTeamSelection && (
                <AssignTeamToProductModal
                    open={showTeamConfirmationModal}
                    onSkip={() => {
                        setShowTeamConfirmationModal(false);
                        teamModalResolveRef.current?.({ action: 'skip' });
                        teamModalResolveRef.current = null;
                    }}
                    onConfirm={(teamId) => {
                        setShowTeamConfirmationModal(false);
                        teamModalResolveRef.current?.({ action: 'confirm', teamId });
                        teamModalResolveRef.current = null;
                    }}
                    onClose={() => {
                        setShowTeamConfirmationModal(false);
                        teamModalResolveRef.current?.({ action: 'abort' });
                        teamModalResolveRef.current = null;
                    }}
                />
            )}

            {/* Mounted only while open, so its selection and search reset on close */}
            {isApplyDrawerOpen && (
                <ApplyItemTemplateDrawer
                    open
                    onClose={() => setIsApplyDrawerOpen(false)}
                    onApply={handleApplyTemplate}
                />
            )}
        </div>
    );
};
export default Products;

function useProductsContext() {
    return useOutletContext<{
        onEdit: (id: string) => Promise<void>;
        loading?: boolean;
        viewOnly?: boolean;
        setActiveProductParams?: (params: Partial<PaginatedParams> | undefined) => void;
        revalidateItems?: () => Promise<void>;
    }>();
}
